<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const emit = defineEmits(['error']);
const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const hasError = ref(false);
const showGallery = ref(false);
const isDownloading = ref(false);
let pollingInterval = null;

// Détecter si le fichier est en cours de chargement (dataUrl vide mais fichier existant)
const isLoading = computed(() => {
  return !attachment.value?.dataUrl && attachment.value?.id;
});

// Polling pour vérifier si dataUrl est maintenant disponible
const startPolling = () => {
  if (pollingInterval) return;

  pollingInterval = setInterval(() => {
    if (attachment.value?.dataUrl) {
      // dataUrl est maintenant disponible, arrêter le polling
      stopPolling();
    }
  }, 2000); // Vérifier toutes les 2 secondes
};

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

// Démarrer le polling quand isLoading devient true
watch(isLoading, newValue => {
  if (newValue) {
    startPolling();
  } else {
    stopPolling();
  }
});

// Démarrer le polling au montage si nécessaire
onMounted(() => {
  if (isLoading.value) {
    startPolling();
  }
});

// Nettoyer le polling au démontage
onUnmounted(() => {
  stopPolling();
});

const handleError = () => {
  hasError.value = true;
  emit('error');
  stopPolling();
};

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isLoading"
      class="flex flex-col items-center justify-center gap-2 p-8 text-center rounded-lg bg-n-slate-2"
    >
      <Icon icon="i-lucide-loader-2" class="text-n-slate-11 animate-spin" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.LOADING_IMAGE') }}
      </p>
    </div>
    <div v-else class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
        @click.stop="showGallery = true"
        @error="handleError"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
